import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchUsers, grantUser, deleteUser } from '../lib/api';
import type { GrantRequest } from '../types/api';
import { useState } from 'react';

export default function UsersTable() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const limit = 50;

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-users', page, limit],
    queryFn: () => fetchUsers(limit, page * limit),
  });

  const grantMutation = useMutation({
    mutationFn: ({ userId, request }: { userId: string; request: GrantRequest }) =>
      grantUser(userId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowGrantDialog(null);
      setGrantRequest({ months: 1, reason: '', tier: 'plus' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowDeleteDialog(null);
    },
  });

  const [showGrantDialog, setShowGrantDialog] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null);
  const [grantRequest, setGrantRequest] = useState<GrantRequest>({
    months: 1,
    reason: '',
    tier: 'plus',
  });

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p className="mt-2 text-sm text-gray-500">Loading users...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <p className="text-sm text-red-800">
          Error loading users: {error.message}
        </p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const handleGrant = () => {
    if (!showGrantDialog) return;
    if (grantRequest.months < 1 || grantRequest.months > 120) {
      alert('Months must be between 1 and 120');
      return;
    }
    if (grantRequest.reason.length < 5 || grantRequest.reason.length > 500) {
      alert('Reason must be between 5 and 500 characters');
      return;
    }
    grantMutation.mutate({ userId: showGrantDialog, request: grantRequest });
  };

  const handleDelete = () => {
    if (!showDeleteDialog) return;
    deleteMutation.mutate({ userId: showDeleteDialog });
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                User
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Subscription
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Firebase
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                RevenueCat
              </th>
              <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Created
              </th>
              <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {data.users.map((user) => (
              <tr key={user.id}>
                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                  <div>
                    <div className="font-medium text-gray-900">{user.email || user.firebase_uid}</div>
                    <div className="text-gray-500">{user.display_name || 'No name'}</div>
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {user.subscription_tier ? (
                    <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                      {user.subscription_tier}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                      Free
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {user.matched_firebase ? (
                    <span className="text-green-600">✓</span>
                  ) : (
                    <span className="text-red-600">✗</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {user.matched_revenuecat ? (
                    <span className="text-green-600">✓</span>
                  ) : (
                    <span className="text-red-600">✗</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                  <button
                    onClick={() => setShowGrantDialog(user.id)}
                    className="text-indigo-600 hover:text-indigo-900 mr-4"
                  >
                    Grant
                  </button>
                  <button
                    onClick={() => setShowDeleteDialog(user.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-6">
        <div className="text-sm text-gray-500">
          Showing {page * limit + 1} to {Math.min((page + 1) * limit, data.pagination.total)} of {data.pagination.total} users
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            className="px-3 py-1 text-sm border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => page + 1 && setPage(page + 1)}
            disabled={!data.pagination.hasMore}
            className="px-3 py-1 text-sm border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Grant Dialog */}
      {showGrantDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Grant Subscription</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Tier</label>
                <select
                  value={grantRequest.tier}
                  onChange={(e) => setGrantRequest({ ...grantRequest, tier: e.target.value as 'plus' | 'premium' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                >
                  <option value="plus">Plus</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Months</label>
                <input
                  type="number"
                  min="1"
                  max="120"
                  value={grantRequest.months}
                  onChange={(e) => setGrantRequest({ ...grantRequest, months: parseInt(e.target.value) || 1 })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Reason</label>
                <textarea
                  value={grantRequest.reason}
                  onChange={(e) => setGrantRequest({ ...grantRequest, reason: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                  rows={3}
                  placeholder="Provide a reason for this grant (5-500 characters)"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowGrantDialog(null);
                  setGrantRequest({ months: 1, reason: '', tier: 'plus' });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGrant}
                disabled={grantMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {grantMutation.isPending ? 'Granting...' : 'Grant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-red-900 mb-4">Delete User</h3>
            <p className="text-sm text-gray-600 mb-4">
              This action will permanently delete the user and all their data. This action cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteDialog(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
